import { Link } from 'react-router-dom';
import { AutoDealReview } from './AutoDealReview';

const TOOL_SECTIONS: Array<{
  title: string;
  note: string;
  tools: Array<{ title: string; purpose: string; to: string; badge: string; tone: 'read' | 'write' }>;
}> = [
  {
    title: 'Read-only readouts',
    note: 'Source-labeled, read-only. Diagnose operational state — opening these changes nothing.',
    tools: [
      {
        title: 'Build readiness readout',
        purpose: 'Read-only active builds, materials readiness, and handoff risk from Odoo-side projections.',
        to: '/ops',
        badge: 'Read-only',
        tone: 'read',
      },
    ],
  },
  {
    title: 'Write & approval tools',
    note: 'These change app or Odoo state. Confirm the source of truth and action boundary before you act.',
    tools: [
      {
        title: 'Procurement triage',
        purpose: 'Mixed-source procurement risk from Monday, Odoo, purchasing email, and chat. Creating or editing a requisition writes a Sales-QA record — POs and Odoo procurement stay outside this app.',
        to: '/procurement',
        badge: 'Writes app requisitions',
        tone: 'write',
      },
      {
        title: 'Odoo product proposals',
        purpose: 'Review candidate product records from vendor URLs and texted product signals. Approval writes product data to Odoo; package and BOM readiness stay in Build Studio.',
        to: '/product-proposals',
        badge: 'Odoo product write',
        tone: 'write',
      },
    ],
  },
  {
    title: 'Data maintenance',
    note: 'Manual evidence and repair paths. Use only when the normal evidence flow missed something.',
    tools: [
      {
        title: 'Manual evidence',
        purpose: 'Create or review human-entered engagement records.',
        to: '/engagements',
        badge: 'Writes records',
        tone: 'write',
      },
      {
        title: 'CSV import',
        purpose: 'Load structured source data when no automated importer exists. Dry-run preview before any write.',
        to: '/imports',
        badge: 'Dry-run, then writes',
        tone: 'write',
      },
      {
        title: 'Merge queue',
        purpose: 'Inspect duplicate-customer identity risk. Merge execution is intentionally design-gated.',
        to: '/merges',
        badge: 'Design-gated',
        tone: 'read',
      },
    ],
  },
  {
    title: 'Legacy mirrors',
    note: 'Compatibility surfaces that should not be treated as primary workflow homes.',
    tools: [
      {
        title: 'Legacy pipeline mirror',
        purpose: 'Inspect mirrored deal data while QT remains the action cockpit and Odoo becomes the record.',
        to: '/pipeline',
        badge: 'Read-only mirror',
        tone: 'read',
      },
    ],
  },
];

export function SystemToolsHub(): JSX.Element {
  return (
    <div className="space-y-6">
      {/* B5: relocated here from /pipeline so the legacy mirror is genuinely read-only (ADR-0003).
          Stub-deal review disposes of Sales-QA-native staging objects (never QT deals); self-hides when none pending. */}
      <AutoDealReview />

      <header>
        <div className="text-xs font-semibold uppercase tracking-wide text-cyan-700">
          System tools
        </div>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">
          Maintenance and Legacy Tools
        </h1>
        <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">
          These are support surfaces for evidence, operations, and data repair. Each tool should make its source of
          truth and action boundary obvious before anyone changes state.
        </p>
      </header>

      <div className="space-y-5">
        {TOOL_SECTIONS.map((section) => (
          <section key={section.title} className="rounded-lg bg-white shadow-sm ring-1 ring-slate-200">
            <header className="border-b border-slate-200 bg-slate-50 px-5 py-4">
              <h2 className="text-base font-semibold text-slate-950">{section.title}</h2>
              <p className="mt-1 text-xs leading-5 text-slate-600">{section.note}</p>
            </header>
            <div className="grid gap-0 divide-y divide-slate-100 lg:grid-cols-3 lg:divide-x lg:divide-y-0">
              {section.tools.map((tool) => (
                <Link
                  key={tool.to}
                  to={tool.to}
                  className="block p-5 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400"
                >
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-950">{tool.title}</span>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${
                        tool.tone === 'write'
                          ? 'bg-amber-50 text-amber-900 ring-amber-200'
                          : 'bg-slate-100 text-slate-600 ring-slate-200'
                      }`}
                    >
                      {tool.badge}
                    </span>
                  </span>
                  <span className="mt-1 block text-sm leading-6 text-slate-600">{tool.purpose}</span>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
